import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import AppNav from "@/components/site/AppNav";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Check, ArrowRight, Lock } from "lucide-react";
import { trackInitiateCheckout } from "@/lib/analytics";

export default function Billing() {
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialPlan = params.get("plan") || "";
  const sessionIdFromUrl = params.get("session_id");
  const canceled = params.get("canceled");

  const [plans, setPlans] = useState([]);
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState(initialPlan);
  const [busy, setBusy] = useState(false);
  const [pollStatus, setPollStatus] = useState(null);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.plans));
    api.get("/config").then((r) => setConfig(r.data));
  }, []);

  const plan = useMemo(() => plans.find((p) => p.code === selected), [plans, selected]);
  const paymentsEnabled = config?.payments_enabled;

  // Poll payment status after redirect from Stripe
  const pollStatusCall = useCallback(
    async (sessionId, attempts = 0) => {
      const max = 8;
      if (attempts >= max) {
        setPollStatus({ kind: "error", message: "Payment status check timed out. Refresh to retry." });
        return;
      }
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setPollStatus({ kind: "success", message: `You're on ${res.data.plan_code.toUpperCase()}. Welcome to Draftwave.` });
          toast.success("Payment successful");
          await refresh();
          return;
        }
        if (res.data.status === "expired") {
          setPollStatus({ kind: "error", message: "Payment session expired." });
          return;
        }
        setPollStatus({ kind: "pending", message: "Confirming with Stripe…" });
        setTimeout(() => pollStatusCall(sessionId, attempts + 1), 2000);
      } catch (e) {
        setPollStatus({ kind: "error", message: e?.response?.data?.detail || "Status check failed" });
      }
    },
    [refresh]
  );

  useEffect(() => {
    if (sessionIdFromUrl) {
      setPollStatus({ kind: "pending", message: "Confirming payment…" });
      pollStatusCall(sessionIdFromUrl);
      // Clean the URL after starting to poll
      const cleaned = new URLSearchParams(params);
      cleaned.delete("session_id");
      cleaned.delete("plan");
      setParams(cleaned, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionIdFromUrl]);

  useEffect(() => {
    if (canceled) {
      toast.info("Payment canceled. Try again when ready.");
      const cleaned = new URLSearchParams(params);
      cleaned.delete("canceled");
      setParams(cleaned, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canceled]);

  async function handleCheckout() {
    if (!plan) return;
    setBusy(true);
    try {
      trackInitiateCheckout(plan.code, plan.price_cents / 100);
      const res = await api.post("/checkout/session", {
        plan_code: plan.code,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Checkout failed");
      setBusy(false);
    }
  }

  async function handleCancel() {
    if (!window.confirm("Cancel your subscription? You'll drop to the free tier at end of period.")) return;
    setBusy(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Subscription canceled");
      await refresh();
    } catch {
      toast.error("Cancel failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background" data-testid="billing-page">
      <AppNav />
      <main className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12">
        <section className="lg:col-span-8 border-r border-border p-8 md:p-12">
          <div className="overline text-muted-foreground">Billing · Stripe</div>
          <h1 className="font-serif-display text-4xl md:text-6xl leading-none tracking-tight mt-4">
            Turn on
            <br />
            recurring revenue.
          </h1>

          {pollStatus && (
            <div
              className={`mt-8 border p-4 flex items-start gap-3 ${
                pollStatus.kind === "success"
                  ? "border-primary bg-primary/5"
                  : pollStatus.kind === "error"
                    ? "border-destructive bg-destructive/5"
                    : "border-border"
              }`}
              data-testid="poll-status"
            >
              {pollStatus.kind === "pending" ? (
                <Loader2 className="w-4 h-4 mt-0.5 animate-spin" />
              ) : pollStatus.kind === "success" ? (
                <Check className="w-4 h-4 mt-0.5 text-primary" />
              ) : (
                <Lock className="w-4 h-4 mt-0.5" />
              )}
              <div className="text-sm">{pollStatus.message}</div>
            </div>
          )}

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-t border-border">
            {plans.map((p) => (
              <button
                key={p.code}
                onClick={() => setSelected(p.code)}
                className={`p-6 text-left border-r border-b border-border ${selected === p.code ? "bg-foreground text-background" : "hover:bg-muted"}`}
                data-testid={`select-plan-${p.code}`}
              >
                <div className="overline opacity-60">{p.name}</div>
                <div className="font-serif-display text-3xl mt-2">
                  {p.price_display}
                  <span className="text-sm opacity-60"> / mo</span>
                </div>
                <div className="text-xs mt-2 opacity-70">
                  {p.monthly_generations} texts · {p.monthly_images} images
                </div>
              </button>
            ))}
          </div>

          {plan && (
            <div className="mt-10 border border-border p-6" data-testid="checkout-container">
              <div className="overline text-muted-foreground mb-4">
                Secure checkout · Powered by Stripe
              </div>

              {!paymentsEnabled ? (
                <div className="border border-dashed border-border p-6 text-sm">
                  <div className="flex items-center gap-2 font-medium">
                    <Lock className="w-4 h-4" /> Payments not configured
                  </div>
                  <p className="mt-2 text-muted-foreground">
                    Ask the admin to add <span className="font-mono">STRIPE_API_KEY</span> to the backend env.
                  </p>
                </div>
              ) : (
                <>
                  <ul className="mt-2 space-y-2 text-sm">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className="w-4 h-4 mt-0.5 text-primary" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="rule-h my-6" />
                  <div className="flex items-baseline justify-between">
                    <div>
                      <div className="overline text-muted-foreground">Due today</div>
                      <div className="font-serif-display text-4xl mt-1">
                        {plan.price_display}
                        <span className="text-sm text-muted-foreground"> / month</span>
                      </div>
                    </div>
                    <Button
                      onClick={handleCheckout}
                      disabled={busy}
                      className="rounded-none h-14 px-8 font-mono uppercase tracking-widest text-xs"
                      data-testid="checkout-btn"
                    >
                      {busy ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          Continue to Stripe <ArrowRight className="w-4 h-4 ml-1" />
                        </>
                      )}
                    </Button>
                  </div>
                  <div className="mt-4 text-xs text-muted-foreground">
                    Secure checkout powered by Stripe. Cancel any time.
                  </div>
                </>
              )}
            </div>
          )}
        </section>

        <aside className="lg:col-span-4 p-8 md:p-12">
          <div className="overline text-muted-foreground">Current status</div>
          <div className="mt-4 border border-border p-6" data-testid="current-status-widget">
            <div className="font-serif-display text-3xl capitalize">{user?.plan || "free"}</div>
            <div className="mt-1 overline text-muted-foreground">{user?.plan_status}</div>
            <div className="rule-h my-5" />
            <div className="space-y-3 text-sm font-mono">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Email</span>
                <span className="truncate max-w-[60%]">{user?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Text used</span>
                <span>{user?.monthly_text_used ?? 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Images used</span>
                <span>{user?.monthly_image_used ?? 0}</span>
              </div>
              {user?.current_period_end && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Renews</span>
                  <span>{new Date(user.current_period_end).toLocaleDateString()}</span>
                </div>
              )}
            </div>
            {user?.plan_status === "active" && (
              <Button
                onClick={handleCancel}
                variant="outline"
                className="mt-6 rounded-none h-11 px-5 w-full font-mono uppercase tracking-widest text-xs"
                data-testid="cancel-subscription-btn"
              >
                Cancel subscription
              </Button>
            )}
          </div>
          <p className="mt-6 text-xs text-muted-foreground">
            Each payment covers a 30-day period. Renew any time from this page.
          </p>
        </aside>
      </main>
    </div>
  );
}
